// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { WebStorageStateStore, UserManager, InMemoryWebStorage } from 'oidc-client-ts';
import { PropsWithChildren } from 'react';
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context';
import Loading from '../components/Loading';
import { useIdpInfo, IdpInfo } from '../contexts/IdpContext';

// Earlier releases persisted the OIDC user (including the refresh token) under
// 'oidc.user:*' keys in localStorage. Purge any stale entries so a refresh token
// never lingers in web storage. Other 'oidc.'-prefixed keys hold in-flight
// PKCE/signin state and must be left alone.
// eslint-disable-next-line react-refresh/only-export-components -- exported for direct testing, not a component
export const removeLegacyOidcUsers = () => {
  Object.keys(window.localStorage)
    .filter((key) => key.startsWith('oidc.user:'))
    .forEach((key) => window.localStorage.removeItem(key));
};

const makeUserManager = (idpInfo: IdpInfo) => {
  removeLegacyOidcUsers();

  if (
    !idpInfo ||
    idpInfo.algorithm === 'hs-256-unsafe' ||
    idpInfo.algorithm === 'rs-256-password-grant' ||
    idpInfo.algorithm === 'idp-not-set'
  ) {
    return new UserManager({
      automaticSilentRenew: true,
      response_type: 'code',
      // Keep tokens in memory only so the refresh token never lands in web storage (XSS mitigation).
      userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
      authority: '',
      client_id: '',
      redirect_uri: '',
    });
  }
  return new UserManager({
    automaticSilentRenew: true,
    authority: idpInfo.authority,
    client_id: idpInfo.clientId,
    redirect_uri: idpInfo.redirectUri,
    scope: idpInfo.scope,
    response_type: 'code',
    // Keep tokens in memory only so the refresh token never lands in web storage (XSS mitigation).
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    extraQueryParams: { audience: idpInfo.audience },
  });
};

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { idpInfo } = useIdpInfo();

  if (!idpInfo) {
    return <Loading />;
  }

  // create our own user manager so we can add a userManager.events.addUserLoaded event.
  // unfortunately the normal signinRedirectCallback does not wait to be finished before
  // considering itself authenticated - and we need to wait until ensureUser completes
  // before making any requests
  const userManager = makeUserManager(idpInfo);

  userManager.events.addUserLoaded(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  });

  return <OidcAuthProvider userManager={userManager}>{children}</OidcAuthProvider>;
};

export default AuthProvider;
